import { Box, BoxProps } from '@mui/material';
import { DownloadAppBanner } from 'components/DownloadAppBanner';
import { Loader } from 'components/Loader';
import { InlineStatus } from 'components/InlineStatus';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import { HeaderBar, HeaderBarProps } from './HeaderBar';

export type PageStatus = 'loading' | 'error' | 'unauthorized' | 'ready';

type PageLayoutProps = BoxProps & {
  headerBarProps?: HeaderBarProps;
  allowAppBanner?: boolean;
  status?: PageStatus;
  /**
   * Message shown in the error state. Only rendered when `status === 'error'`,
   * so callers can pass it unconditionally without guarding on `status`.
   */
  errorMessage?: string;
};

type PageContentProps = {
  status: PageStatus;
  errorMessage?: string;
  children: ReactNode;
};

/**
 * Derives the PageStatus from a page's data-fetching flags using a
 * fixed precedence: unauthorized > loading > error > ready.
 */
export const getPageStatus = ({
  isUnauthorized,
  isLoading,
  isError,
}: {
  isUnauthorized?: boolean;
  isLoading?: boolean;
  isError?: boolean;
}): PageStatus => {
  if (isUnauthorized) return 'unauthorized';
  if (isLoading) return 'loading';
  if (isError) return 'error';
  return 'ready';
};

const PageContent = (props: PageContentProps): ReactNode => {
  const { status, errorMessage, children } = props;
  const { t } = useTranslation();

  switch (status) {
    case 'loading':
      return <Loader disableBackground />;
    case 'error':
      return (
        <InlineStatus status="error" fullWidth errorMessage={errorMessage} />
      );
    case 'unauthorized':
      return (
        <InlineStatus
          status="error"
          fullWidth
          errorMessage={t('unauthorized.description')}
        />
      );
    case 'ready':
    default:
      return children;
  }
};

export const PageLayout = (props: PageLayoutProps): JSX.Element => {
  const {
    children,
    status = 'ready',
    headerBarProps,
    errorMessage,
    allowAppBanner = true,
    sx,
    ...otherProps
  } = props;
  const { t } = useTranslation();

  const resolvedHeaderBarProps: HeaderBarProps | undefined = headerBarProps && {
    ...headerBarProps,
    title:
      status === 'unauthorized'
        ? t('unauthorized.title')
        : headerBarProps.title,
  };

  return (
    <Box
      data-cname="PageLayout"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // On WKWebView, after a background -> foreground transition, `100vw` can
        // resolve to a stale layout viewport width (e.g. ~half the screen), squeezing
        // the app. `position: fixed` + `inset: 0` pins to the real viewport edges
        // without relying on viewport units.
        // `html`, `body`, and `#root` are `width`/`height: 100%` (src/css/index.css)
        // so percentage-based descendants resolve against the viewport size.
        position: 'fixed',
        inset: 0,
        bgcolor: 'background.default',
        ...sx,
      }}
      {...otherProps}
    >
      {allowAppBanner && <DownloadAppBanner />}

      {resolvedHeaderBarProps && <HeaderBar {...resolvedHeaderBarProps} />}

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          margin: 0,
          overflow: 'hidden',
          padding: 0,
        }}
      >
        <PageContent status={status} errorMessage={errorMessage}>
          {children}
        </PageContent>
      </Box>
    </Box>
  );
};
